import { useEffect } from "react";
import { motion } from "framer-motion";
import { BadgePercent, Gift, CarFront, Fuel } from "lucide-react";
import PlevenOpenContactForm from "../components/PlevenOpenContactForm";

const MAP_QUERY = "бул. Сторгозия 191, Плевен";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

function PlevenOpen() {
  useEffect(() => {
    document.title = "Ford Плевен | Дни на отворени врати";
  }, []);

  const isAppleDevice =
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);
  const mapsUrl = isAppleDevice
    ? `https://maps.apple.com/?q=${encodeURIComponent(MAP_QUERY)}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        MAP_QUERY,
      )}`;

  return (
    <div className="overflow-hidden bg-white font-fordAntennaRegular text-slate-900">
      <section className="relative w-full text-white isolate bg-fordBlue">
        <div className="relative">
          <picture>
            <source
              media="(max-width: 768px)"
              srcSet="/images/plevenopen/mobile.jpeg"
            />
            <img
              src="/images/plevenopen/1440x516.jpg"
              alt="Ford Плевен"
              className="w-full h-auto"
            />
          </picture>
          <div className="absolute inset-0 bg-[linear-gradient(115deg,rgba(8,20,38,0.66)_0%,rgba(8,20,38,0.5)_42%,rgba(8,20,38,0.18)_100%)]" />
          <div className="absolute inset-x-0 bottom-0 top-0 mx-auto flex max-w-[1400px] items-end px-5 pb-10 pt-16 sm:px-8 lg:px-12 lg:pb-14">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={0}
              className="max-w-3xl"
            >
              <h1 className="max-w-4xl font-fordBlack text-5xl uppercase leading-[0.95] sm:text-6xl lg:text-5xl">
                Дни на отворени врати във Ford Плевен
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="details" className="py-14 bg-white">
        <div className="w-full px-5 sm:px-8 lg:px-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={fadeUp}
            custom={0}
            className="mx-auto max-w-[70%] px-1 text-center"
          >
            <p className="text-[1.05rem] leading-7 text-slate-700">
              Заповядай на Дни на отворени врати във Ford Плевен през целия
              месец юни и разгледай отблизо гамата ни от 100% електрически и
              хибридни автомобили Ford.
            </p>
            <p className="mt-4 text-[1.05rem] leading-7 text-slate-700">
              Запиши се за тест драйв и усети динамиката, комфорта и
              иновациите зад волана на новото поколение автомобили Ford.
            </p>
            <p className="mt-5 text-lg font-bold text-fordBlue">
              Очакваме те!
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={fadeUp}
            custom={0.08}
            className="mt-12"
          >
            <h3 className="text-2xl font-bold leading-tight uppercase text-fordBlue">
              По време на събитието можеш да се възползваш от:
            </h3>
            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              <div className="grid gap-5">
                <div className="group min-h-[180px] rounded-[1.75rem] bg-[rgb(241,241,241)] px-6 py-6 transition-colors duration-200 hover:bg-[rgb(0,9,91)] sm:px-7 sm:py-7">
                  <div className="flex h-12 w-12 items-center justify-center text-[rgb(23,0,244)] transition-colors duration-200 group-hover:text-white">
                    <BadgePercent size={34} strokeWidth={1.5} />
                  </div>
                  <p className="mt-6 text-[0.98rem] font-normal leading-6 text-fordBlue transition-colors duration-200 group-hover:text-white">
                    Ексклузивни цени на избрани модели
                  </p>
                </div>
                <div className="group min-h-[180px] rounded-[1.75rem] bg-[rgb(241,241,241)] px-6 py-6 transition-colors duration-200 hover:bg-[rgb(0,9,91)] sm:px-7 sm:py-7">
                  <div className="flex h-12 w-12 items-center justify-center text-[rgb(23,0,244)] transition-colors duration-200 group-hover:text-white">
                    <Gift size={34} strokeWidth={1.5} />
                  </div>
                  <p className="mt-6 text-[0.98rem] font-normal leading-6 text-fordBlue transition-colors duration-200 group-hover:text-white">
                    Подарък при покупка на нов автомобил - 1 година безплатно
                    сервизиране
                  </p>
                </div>
              </div>

              <div className="grid gap-5">
                <div className="group min-h-[180px] rounded-[1.75rem] bg-[rgb(241,241,241)] px-6 py-6 transition-colors duration-200 hover:bg-[rgb(0,9,91)] sm:px-7 sm:py-7">
                  <div className="flex h-12 w-12 items-center justify-center text-[rgb(23,0,244)] transition-colors duration-200 group-hover:text-white">
                    <CarFront size={34} strokeWidth={1.5} />
                  </div>
                  <p className="mt-6 text-[0.98rem] font-normal leading-6 text-fordBlue transition-colors duration-200 group-hover:text-white">
                    Възможност за тест драйв преди покупка
                  </p>
                </div>
                <div className="group min-h-[180px] rounded-[1.75rem] bg-[rgb(241,241,241)] px-6 py-6 transition-colors duration-200 hover:bg-[rgb(0,9,91)] sm:px-7 sm:py-7">
                  <div className="flex h-12 w-12 items-center justify-center text-[rgb(23,0,244)] transition-colors duration-200 group-hover:text-white">
                    <Fuel size={34} strokeWidth={1.5} />
                  </div>
                  <p className="mt-6 text-[0.98rem] font-normal leading-6 text-fordBlue transition-colors duration-200 group-hover:text-white">
                    По-ниски разходи за гориво с електрически или хибриден
                    автомобил
                  </p>
                </div>
              </div>

              <div className="rounded-[1.75rem] bg-[rgb(0,9,91)] px-6 py-7 sm:px-7 lg:min-h-full">
                <p className="mt-3 text-lg leading-8 text-white">
                  Всеки ден от 1 до 30 юни,
                  <br />
                  между 09:00 и 18:00 ч.
                </p>
                <p className="mt-12 text-lg leading-8 text-white">
                  гр. Плевен, бул. „Сторгозия“ 191,
                  <br />
                  Северна промишлена зона
                </p>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-6 text-sm text-gray-300 font-fordAntennaRegular hover:underline"
                >
                  Виж на картата
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="form" className="py-24 bg-white">
        <div className="mx-auto max-w-[1120px] px-5 sm:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={0}
            className="w-full"
          >
            <PlevenOpenContactForm />
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default PlevenOpen;
